import {
  DefaultMetricOptions,
  EntityLinks,
  Goal,
  GoalCompletion,
  GoalProgressCount,
  GoalTreeIndex,
  GoalType,
  ID,
} from '@griply/types';

import { calculateMetricEntryValue } from './calculate-metric-entry-value';
import { getSubGoals } from './goals/get-sub-goals';

export type GetGoalCompletionOptions = {
  links?: EntityLinks;
  goals: Goal[];
  goalProgressCounts?: Map<ID, GoalProgressCount>;
  index?: GoalTreeIndex;
};

const getProgressCountCompletion = (
  goalId: ID,
  goalProgressCounts?: Map<ID, GoalProgressCount>,
): GoalCompletion => {
  // no live counts yet — report an unmeasurable target rather than a false 0%
  if (!goalProgressCounts) {
    return { start: 0, current: 0, end: Infinity };
  }

  const { completed, total } = goalProgressCounts.get(goalId) ?? {
    completed: 0,
    total: 0,
  };

  return { start: 0, current: completed, end: total };
};

export const getGoalCompletion = (
  goal: Goal,
  { goals, goalProgressCounts, index, links }: GetGoalCompletionOptions,
): GoalCompletion => {
  // The index is keyed on goals, so a task-measured cache entry goes stale unseen.
  const isCacheable =
    goal.type !== GoalType.Project &&
    goal.metric?.id !== DefaultMetricOptions.CompletedTasks;

  const cached = isCacheable
    ? index?.completionByGoalId.get(goal.id)
    : undefined;
  if (cached) {
    return cached;
  }

  const completion = computeGoalCompletion(
    goal,
    goals,
    goalProgressCounts,
    index,
    links,
  );
  if (isCacheable) {
    index?.completionByGoalId.set(goal.id, completion);
  }

  return completion;
};

const computeGoalCompletion = (
  goal: Goal,
  goals: Goal[],
  goalProgressCounts?: Map<ID, GoalProgressCount>,
  index?: GoalTreeIndex,
  links?: EntityLinks,
): GoalCompletion => {
  // Projects ignore any metric carried over from when the item was a goal
  if (goal.type === GoalType.Project) {
    return getProgressCountCompletion(goal.id, goalProgressCounts);
  }

  if (!goal.metric?.id) {
    return {
      start: 0,
      current: 0,
      end: Infinity,
    };
  }

  if (goal.metric?.id === DefaultMetricOptions.CompletedTasks) {
    return getProgressCountCompletion(goal.id, goalProgressCounts);
  }

  if (goal.metric?.id === DefaultMetricOptions.SubGoalProgress) {
    const subGoals = getSubGoals(goal.id, {
      goals,
      recursive: true,
      index,
      links,
    });

    return {
      start: 0,
      current: subGoals.filter(({ completedAt }) => !!completedAt).length,
      end: subGoals.length,
    };
  }

  const start = goal.metric?.startValue ?? 0;
  const lastEntry = goal.metric.entries
    ?.slice()
    .sort((entryA, entryB) => entryB.date.getTime() - entryA.date.getTime())[0];
  const current = lastEntry
    ? calculateMetricEntryValue(lastEntry.id, goal.metric.entries!, start)
    : start;

  return {
    start,
    end: goal.metric?.targetValue ?? Infinity,
    current,
  };
};
